import React from "react";
import { Box, Button, Typography, Paper, Container } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  BugReportOutlined as BugReportIcon,
  ArrowForward as ArrowForwardIcon,
  IntegrationInstructions as IntegrationInstructionsIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SystemBugEmptyState: React.FC = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);

  const handleNavigateToSetup = () => {
    navigate(`/defect-tracker/projects/${projectId}/settings/sniffer-autobug`);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background.default,
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 152, 0, 0.15)"
                      : "rgba(255, 152, 0, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      opacity: 1,
                    },
                    "50%": {
                      transform: "scale(1.05)",
                      opacity: 0.8,
                    },
                  },
                }}
              >
                <IntegrationInstructionsIcon
                  sx={{
                    fontSize: 50,
                    color: theme.palette.warning.main,
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            {t("Setup Required")}
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 1,
              lineHeight: 1.7,
            }}
          >
            {t("systemBugEmptyDescription")}
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            {t("systemBugEmptyDocHint")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNavigateToSetup}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  transform: "translateX(4px)",
                  transition: "all 0.2s ease-in-out",
                },
              }}
            >
              {t("Go to Setup Documentation")}
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(33, 150, 243, 0.12)"
                    : "rgba(33, 150, 243, 0.05)",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(33, 150, 243, 0.3)" : "rgba(33, 150, 243, 0.15)"}`,
              }}
            >
              <BugReportIcon
                sx={{
                  color: theme.palette.info.main,
                  fontSize: 20,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("Once configured, automatic bug reports will appear here")}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SystemBugEmptyState;
